import { useState, useCallback, useMemo } from "react";
import debounce from "lodash.debounce";

type FetchFn = (params: {
  page: number;
  search: string;
}) => Promise<{
  options: any[];
  hasMore: boolean;
}>;

export const useInfiniteSelect = (fetchFn: FetchFn) => {
  const [options, setOptions] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Load initial data
  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchFn({ page: 1, search: "" });
      setOptions(res.options);
      setHasMore(res.hasMore);
      setPage(1);
      setSearch("");
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  // Load more for infinite scroll
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    const nextPage = page + 1;
    setLoading(true);
    try {
      const res = await fetchFn({
        page: nextPage,
        search,
      });

      setOptions((prev) => [...prev, ...res.options]);
      setHasMore(res.hasMore);
      setPage(nextPage);
    } finally {
      setLoading(false);
    }
  }, [page, search, hasMore, loading, fetchFn]);

  // Debounced search function
  const searchFn = useCallback(
    async (value: string) => {
      setSearch(value);
      setLoading(true);

      try {
        const res = await fetchFn({
          page: 1,
          search: value,
        });

        setOptions(res.options);
        setHasMore(res.hasMore);
        setPage(1);
      } finally {
        setLoading(false);
      }

      return value;
    },
    [fetchFn]
  );

  // Create a debounced version to prevent multiple rapid calls
  const onSearch = useMemo(() => debounce(searchFn, 400), [searchFn]);

  return {
    options,
    loading,
    loadInitial,
    loadMore,
    onSearch,
  };
};
